import {useIdFromRoute} from '@fl/cmsch-fe-library';
import {isEmpty} from 'lodash/fp';
import {MouseEvent, useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {AnimalType} from 'api/gen/AnimalType';
import {EarTag} from 'api/gen/EarTag';
import {Sex} from 'api/gen/Sex';
import {TFunction, useOurTranslation} from 'app/translations';

import {animalBrowserAction} from '../model';
import {simpleAnimalBrowserSelector} from '../model/selector';
import {MotherLinkProps} from '../types/mother-link-props';

interface UseAnimalsBrowserBasicSetup {
    tCommon: TFunction<'common'>;
    t: TFunction<'animalBrowser'>;
    disabledExportTitle: string;
    exportDisabled: boolean;
    femalesForExport: Array<EarTag>;
    bullsForExport: Array<EarTag>;
    contentForPdfExportIsFilled: boolean;
    id: number;
    handleOpenExportPdfModal(): void;
    handleCloseModal(): void;
    handleSubmitModal(sex: Sex): void;
    exportToCSV(): void;
    handleMotherLink(row: MotherLinkProps): (e: MouseEvent) => void;
}

/**
 * Provides shared export, print, and mother-link wiring for the basic animal-browser tables.
 *
 * @param animalType Basic browser animal type that determines the export and print flow.
 * @returns Shared translations, export flags, modal handlers, route id, and mother-link callback.
 */
/* eslint-disable-next-line max-lines-per-function */
export const useAnimalsBrowserBasicSetup = (animalType: AnimalType): UseAnimalsBrowserBasicSetup => {
    const dispatch = useDispatch();
    const isFromCows = animalType === 'BREEDING_FEMALES';
    const femalesForExport = useSelector(simpleAnimalBrowserSelector.femalesForExport);
    const bullsForExport = useSelector(simpleAnimalBrowserSelector.bullsForExport);
    const contentForPdfExportIsFilled = !isEmpty(femalesForExport) || !isEmpty(bullsForExport);
    const id = useIdFromRoute();

    const exportToCSV = useCallback(() => {
        dispatch(isFromCows
            ? animalBrowserAction.exportToCsv(id)
            : animalBrowserAction.getCalvesCsv(id),
        );
    }, [dispatch, id, isFromCows]);

    const handleMotherLink = useCallback((row: MotherLinkProps) => (e: MouseEvent) => {
        e.preventDefault();
        dispatch(animalBrowserAction.getMotherHuid(row.motherEarTag ?? '', row.huid ?? '', animalType, 'BASIC'));
    }, [dispatch, animalType]);

    const handleCloseModal = useCallback(() => {
        dispatch(animalBrowserAction.setBullsForExport([]));
        dispatch(animalBrowserAction.setFemalesForExport([]));
    }, [dispatch]);

    const handleSubmitModal = useCallback((sex: Sex) => {
        dispatch(animalBrowserAction.submitPrintModal(sex));
    }, [dispatch]);

    const handleOpenExportPdfModal = useCallback(() => {
        dispatch(isFromCows
            ? animalBrowserAction.getBreedingFemalesForPrint(id)
            : animalBrowserAction.getAnimalsForPrint(id),
        );
    }, [dispatch, id, isFromCows]);

    const {t, tCommon} = useOurTranslation('animalBrowser');
    const animalBrowserState = useSelector(simpleAnimalBrowserSelector.animalBrowser);

    const {exportInProgress, printInProgress, cowBrowserPage, calfBrowserPage} = animalBrowserState;
    const page = isFromCows ? cowBrowserPage : calfBrowserPage;
    const content = page.prop('content').orElse([]);
    const exportDisabled = isEmpty(content) || exportInProgress || printInProgress;
    const disabledExportTitle = t(exportInProgress ? 'exportInProgress' : 'exportDisabled');

    return {
        tCommon,
        t,
        disabledExportTitle,
        exportDisabled,
        exportToCSV,
        handleMotherLink,
        femalesForExport,
        bullsForExport,
        contentForPdfExportIsFilled,
        handleCloseModal,
        handleSubmitModal,
        handleOpenExportPdfModal,
        id,
    };
};
